import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import buildUrl from "build-url-ts";

import DataSourceCard from "./components/dataSourceCard";
import MigrationSlider from "./components/migrationSlider";
import RssSlider from "./components/rssSlider";
import Toast from "src/components/toast";
import StorageTracker from "./components/storageTracker";
import Tooltip from "src/components/tooltip";

import { useAuthenticateDataSource, useCreateRssDataSource, useDataSourceQuery } from "src/query/settings/dataSource";
import { useWebSocketSubscription } from "src/query/webSocket";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSourceTracking } from "src/query/settings/adoption/contentSources";

import { getStorageDetails, StorageType } from "src/utils/dataSource.helper";
import { MigrationProgress, StorageAction, ToolbarProps } from "src/types/settings/dataSource";
import subscriptionPlanService from "src/utils/subscriptionPlan.service";

import styles from "./styles.module.scss"
import cx from "classnames";

const apiUrl = `${process.env.REACT_APP_API_URL}`;

const DROPBOX_OAUTH_PENDING_KEY = "pf_datasource_dropbox_oauth_status";

const DROPBOX_OAUTH_PENDING_VALUE = "pending";

const OAUTH_QUERY_KEYS = ["code", "state", "error", "error_description", "error_uri"];

function removeOAuthParamsFromSearch(search: string): string {
  const params = new URLSearchParams(search);
  OAUTH_QUERY_KEYS.forEach((key) => params.delete(key));
  const queryString = params.toString();
  return queryString ? `?${queryString}` : "";
}

const DataSource = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const sourceTypeRef = useRef<StorageType | null>(null);

  const storageTypes: StorageType[] = [
    StorageType.DROPBOX,
    StorageType.GDRIVE,
    StorageType.BOX,
    StorageType.ONEDRIVE,
    StorageType.SHAREPOINT,
    StorageType.RSS,
  ];

  const storageActions = storageTypes.map(type => getStorageDetails(type));

  const { data: items = [], isLoading: isDataSourceLoading, refetch } = useDataSourceQuery();
  const createRssMutation = useCreateRssDataSource();
  const authenticateMutation = useAuthenticateDataSource();
  const { trackSourceTypeSelected, trackSourceConnected, trackContentSourceClicked } = useSourceTracking();

  const [sourceType, setSourceType] = useState<StorageType | null>(null);
  const [showRssSlider, setShowRssSlider] = useState(false);
  const [showMigrationSlider, setShowMigrationSlider] = useState(false);
  const [activeMigrationItem, setActiveMigrationItem] = useState<any | null>(null);

  const completeAuthWithCode = useCallback(
    (sourceType: StorageType, code: string) => {
      const label = getStorageDetails(sourceType).name;
      trackSourceConnected({ sourceType });
      authenticateMutation.mutate(
        { sourceType, code },
        {
          onSuccess: (data) => {
            toast(
              <Toast.Sucess
                heading="Source Connected"
                message={`${data?.name}'s ${data?.storageName} account authorized successfully.`}
              />
            );
            refetch();
          },
          onError: () => {
            toast(
              <Toast.Failure
                heading="Authorization Failed"
                message={`Failed to connect ${label}.`}
              />
            );
          },
        }
      );
    },
    [authenticateMutation, refetch, trackSourceConnected]
  );

  const handleCodeReceivedEvent = useCallback(
    (event: MessageEvent) => {
      const { code, error } = event.data?.payload || {};

      if (error) {
        toast(<Toast.Failure heading="Auth Failed" message={error} />);
        return;
      }

      if (code && sourceTypeRef.current) {
        completeAuthWithCode(sourceTypeRef.current, code);
        window.removeEventListener("message", handleCodeReceivedEvent);
      }
    },
    [completeAuthWithCode]
  );

  const handleSourceAuthenticate = (dataSource: StorageAction) => {
    trackSourceTypeSelected({ sourceType: dataSource.storageType });
    if (dataSource.storageType !== "rss") {

      sourceTypeRef.current = dataSource.storageType;

      const url = buildUrl(apiUrl, {
        path: `datasources/${dataSource.storageType}/authorize`,
      });

      // Dropbox: full-page redirect (no popup / postMessage). After OAuth, the backend must
      // redirect the browser to this app route with ?code= / ?error= query params (see useEffect below).
      if (dataSource.storageType === StorageType.DROPBOX) {
        try {
          sessionStorage.setItem(
            DROPBOX_OAUTH_PENDING_KEY,
            DROPBOX_OAUTH_PENDING_VALUE
          );
        } catch (error) {
          console.error("Error setting session storage", error);
        }
        window.location.assign(url);
        return;
      }
      const dualScreenLeft = window.screenLeft ?? window.screenX ?? 0;
      const dualScreenTop = window.screenTop ?? window.screenY ?? 0;

      const screenWidth = window.screen.width;
      const screenHeight = window.screen.height;

      const w = Math.floor(screenWidth * 0.8);
      const h = Math.floor(screenHeight * 0.8);

      const left = dualScreenLeft + (screenWidth - w) / 2;
      const top = dualScreenTop + (screenHeight - h) / 2;

      const newWindow = window.open(
        url,
        `Authorize ${dataSource.name} Account`,
        `scrollbars=yes, width=${w}, height=${h}, top=${top}, left=${left}`
      );

      newWindow?.focus();
      window.addEventListener("message", handleCodeReceivedEvent, false);
    } else {
      setSourceType(dataSource.storageType);
      setShowRssSlider(true);
    }
  };

  const handleRssSave = (rssUrl: string) => {

    createRssMutation.mutate(
      { sourceType: "rss", rssUrl },
      {
        onSuccess: (data) => {
          refetch();
          toast(
            <Toast.Sucess
              heading="RSS Source Created"
              message="The RSS data source has been successfully added."
            />
          );
          setShowRssSlider(false);
        },
        onError: (error) => {
          console.error("Error creating RSS data source", error);
          toast(
            <Toast.Failure
              heading="Failed to create RSS Source"
              message="Something went wrong. Please try again."
            />
          );
        },
      }
    );
  };

  useEffect(() => {
    try {
      const oauthStatus = sessionStorage.getItem(DROPBOX_OAUTH_PENDING_KEY);
  
      // Only run if OAuth was actually initiated by us
      if (oauthStatus !== DROPBOX_OAUTH_PENDING_VALUE) return;
  
      const params = new URLSearchParams(location.search);
      const code = params.get("code");
      const oauthError = params.get("error");
      const errorDescription = params.get("error_description");
  
      // Nothing to process
      if (!code && !oauthError) return;
  
      // Prevent duplicate processing
      const fingerprint = `${code ?? ""}|${oauthError ?? ""}|${params.get("state") ?? ""}`;
      const processedKey = `pf_dropbox_oauth_processed_${fingerprint}`;
  
      if (sessionStorage.getItem(processedKey)) {
        const cleanSearch = removeOAuthParamsFromSearch(location.search);
        navigate(
          { pathname: location.pathname, search: cleanSearch },
          { replace: true }
        );
        return;
      }
  
      sessionStorage.setItem(processedKey, "processed");
      sessionStorage.removeItem(DROPBOX_OAUTH_PENDING_KEY);
  
      // Clean URL
      const cleanSearch = removeOAuthParamsFromSearch(location.search);
      navigate(
        { pathname: location.pathname, search: cleanSearch },
        { replace: true }
      );
  
      // Handle error case
      if (oauthError) {
        toast(
          <Toast.Failure
            heading="Auth Failed"
            message={errorDescription || oauthError}
          />
        );
        return;
      }
  
      // Handle success case
      if (code) {
        completeAuthWithCode(StorageType.DROPBOX, code);
      }
    } catch (error) {
      console.error("Error accessing session storage", error);
    }
  }, [location.search, location.pathname, navigate, completeAuthWithCode]);

  const ProgressBar: React.FC<{ percent: number | undefined }> = ({ percent }) => (
    <div className={styles.progressWrapper}>
      <div className={styles.progressBar}>
        <div className={styles.progressFill} style={{ width: `${percent}%` }} />
      </div>
      <div className={styles.progressText}>{percent}%</div>
    </div>
  );
  
  const MigrationProgressStatus: React.FC<{
    sourceId: string;
    totalAssets: number;
    sourceName: string;
    storageType: string;
    onComplete?: () => void;
  }> = ({ sourceId, totalAssets, onComplete, sourceName, storageType }) => {
    const queryClient = useQueryClient();
    const queryKey = ["migrationProgress", sourceId];
  
    // Initialize cache only once
    useEffect(() => {
      const existing = queryClient.getQueryData<MigrationProgress>(queryKey);
      if (!existing) {
        queryClient.setQueryData<MigrationProgress>(queryKey, {
          migratedAssets: 0,
          percentage: 0,
          completed: false,
          initialTotalAssets: totalAssets,
        });
      }
    }, [queryClient, queryKey, totalAssets]);
  
    const parseMessage = useCallback(
      (body: any) => {
        const { type, payload } = body;
        if (type !== "WS_DATASOURCE_MIGRATION_STATUS" || payload?.id !== sourceId)
          return null;
  
        const migrated = payload.migratedAssets ?? 0;
  
        const prev = queryClient.getQueryData<MigrationProgress>(queryKey)!;
  
        const assetsCount = prev.initialTotalAssets;
        const currentMigrated = Math.max(prev.migratedAssets, migrated);
        const completed = payload.completed || currentMigrated >= assetsCount;
  
        const newPercentage = Math.max(
          prev.percentage,
          Math.min(Math.round((currentMigrated / assetsCount) * 100), 100)
        );
  
        return {
          queryKey,
          data: {
            migratedAssets: currentMigrated,
            percentage: newPercentage,
            completed,
            initialTotalAssets: assetsCount,
          },
        };
      },
      [queryClient, queryKey, sourceId]
    );
  
    useWebSocketSubscription<MigrationProgress>(
      "/user/queue/datasourceSync",
      parseMessage,
      (data) => {
        if (!data) return;
    
        const prev = queryClient.getQueryData<MigrationProgress>(queryKey);
        
        if (data.completed && !prev?.toastFired) {
          queryClient.setQueryData(queryKey, {
            ...data,
            toastFired: false,
            toastMeta: {
              heading: "Migration Completed",
              message: `${data.initialTotalAssets} assets migrated for ${sourceName}'s ${storageType}.`
            }
          });
        
          onComplete?.();
          refetch();
        }

        queryClient.setQueryData<MigrationProgress>(queryKey, (old) => ({
          ...(old ?? {}),
          ...data,
          toastFired: data.completed ? true : old?.toastFired,
          initialTotalAssets: old?.initialTotalAssets ?? data.initialTotalAssets,
        }));
      }
    );

    const { data: progress } = useQuery<MigrationProgress>({
      queryKey,
      queryFn: () => queryClient.getQueryData<MigrationProgress>(queryKey)!,
      refetchOnWindowFocus: false,
    });
  
    if (!progress || progress.completed || progress.migratedAssets === 0) {
      return (
        <div className={cx(styles.status, styles.disabled)}>
          <span>Migrating…</span>
        </div>
      );
    }

    return (
      <div className={styles.status}>
        <ProgressBar percent={progress.percentage} />
      </div>
    );
  };
  
  const renderStatus = (status: string, item: any) => {
    if (status === "MIGRATING" || status === "MIGRATION_REQUESTED") {
      return <MigrationProgressStatus sourceId={item.id} totalAssets={item.assets} storageType={item.storageType} sourceName={item.name} onComplete={() => refetch()} />;
    }

    if (status === "MIGRATED" || status === "OK") {
      const isDisabled = item.assets === 0;
      return (
        <div
          className={cx(styles.status, {
            [styles.disabled]: isDisabled,
          })}
          onClick={(e) => {
            if (isDisabled) {
              e.preventDefault();
              e.stopPropagation();
              return;
            }
            setActiveMigrationItem(item);
            setShowMigrationSlider(true);
          }}
        >
          <i className={cx("paperflite-migrate", styles.migrateIcon)} />
          <span>Migrate</span>
        </div>
      );
    }

    if (status === "AUTH_ERROR") {
      return (
        <div className={styles.statusContent}>
          <div className={styles.status} onClick={() => handleSourceAuthenticate(item)}>
            <i className="paperflite-right-arrow" />
            <span>Reauthenticate</span>
          </div>
          <Tooltip content="Your authentication token to this
content source has expired. Please re-authenticate to stay connected to your datasource." placement="auto">
            <i className={cx("paperflite-info", styles.infoIcon)} />
          </Tooltip>
        </div>
      );
    }

    return null;
  };

  useEffect(() => {
    trackContentSourceClicked();
    refetch();
  }
  , []);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.title}>Content Sources</div>
          <div className={styles.subTitle}>Connect your content sources to Paperflite.</div>
        </div>
        <Toolbar
          buttons={storageActions.map(action => ({
            id: action.id,
            image: action.image,
            name: action.name,
            onClick: () => handleSourceAuthenticate(action),
          }))}
        />
      </div>
      <div className={styles.body}>
        <div className={styles.bodyContent}>
          <div className={styles.bodyTitle}>
          Connected Sources ({items.length === 0 ? "0" : items.length.toString().padStart(2, "0")})
          </div>
        <div className={styles.cards}>
          {!isDataSourceLoading && items.length === 0 && (
            <>
              <div className={styles.card}>
                <div className={styles.headerWrapper}>
                  <div className={styles.placeholderHeader}>
                    <div className={styles.placeholderLine} />
                    <div className={styles.placeholderLine} />
                  </div>
                  <div className={styles.iconWrapper}>
                    <div className={styles.placeholderIcon} />
                  </div>
                </div>
                <div className={styles.content}>
                  <div className={styles.placeholderLine} />
                </div>
              </div>

              <div className={styles.card}>
                <div className={styles.emptyState}>
                  <img src="/images/settings/emptyConnectedSources.svg" alt="No source" className={styles.emptyImage} />
                  <div className={styles.emptyText}>no content source connected!</div>
                </div>
              </div>
            </>
          )}

          {items.map((item) => (
            <DataSourceCard
              key={item.id}
              item={item}
              type={item.storageType}
              header={item.name || "Unknown"}
              migrationStatus={item.migrationStatus}
              status={renderStatus(item.migrationStatus, item)}
            />
          ))}
        </div>
        </div>
      </div>
        <MigrationSlider
          show={showMigrationSlider}
          onClose={() => setShowMigrationSlider(false)}
          item={activeMigrationItem}
        />
        <RssSlider
          show={showRssSlider}
          onClose={() => setShowRssSlider(false)}
          onSubmit={handleRssSave}
          isPending={createRssMutation.isPending}
          error={!!createRssMutation.error}
        />
       {!subscriptionPlanService.isLegacyPlan() && (
        <StorageTracker />
       )}
    </div>
  );
}

export default DataSource;

const Toolbar: React.FC<ToolbarProps> = ({ buttons, className }) => {
  return (
    <div className={cx(styles.toolbar, className)}>
      {buttons.map((button, index) => (
        <Tooltip key={button.id} content={button.name} placement="bottom">
          <div key={button.id} className={styles.buttonWrapper} onClick={button.onClick}>
            <img src={button.image} alt={button.id} className={styles.buttonImage} />
            {index < buttons.length - 1 && <div className={styles.divider} />}
          </div>
        </Tooltip>
      ))}
    </div>
  );
};